// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AppIcon,
  Badge,
  Button,
  DataTable,
  IconButton,
  Spinner,
  StatTile,
  StatTileRow,
} from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { fundsApi, FundDTO } from '../api/fundsApi';
import { useTags } from '../contexts/TagsContext';
import { FundTagEditor } from '../components/tags/FundTagEditor';
import { RiskRatingChip } from '../components/funds/RiskRatingChip';
import {
  additionalScalarFields,
  normalizeBreakdown,
  pickFieldSpecs,
  type BreakdownRow,
  type ResolvedField,
} from '../components/funds/fundDisplayRegistry';

interface CalendarYearRow {
  year: string;
  value: string;
}

/** Composition breakdowns to attempt, in display order: [extra_fields key, heading]. */
const BREAKDOWN_SOURCES: [string, string][] = [
  ['top_10_holdings', 'Top holdings'],
  ['holdings', 'Top holdings'],
  ['asset_breakdown', 'Asset allocation'],
  ['strategic_asset_allocation', 'Strategic asset allocation'],
  ['tactical_asset_allocation', 'Tactical asset allocation'],
  ['sector_split', 'Sector allocation'],
];

/**
 * Fund Details — minimal read surface for a single catalogue fund
 * (Fund Center UI 2.0 PRD §5.2). Header identity + favourite/tags, price
 * block, performance tiles, calendar-year history.
 */
export default function FundDetailPage() {
  const { fundKey } = useParams<{ fundKey: string }>();
  const navigate = useNavigate();
  const { featureEnabled: tagsFeatureEnabled } = useTags();

  const [fund, setFund] = useState<FundDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fundKey) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    fundsApi
      .get(fundKey)
      .then((data) => {
        if (!cancelled) setFund(data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setNotFound(true);
        } else {
          setError(e instanceof Error ? e.message : 'Failed to load fund');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundKey]);

  const toggleFavourite = async () => {
    if (!fund) return;
    const wasFavourited = fund.favourited;
    setFund({ ...fund, favourited: !wasFavourited });
    try {
      if (wasFavourited) {
        await fundsApi.removeFavourite(fund.fundKey);
      } else {
        await fundsApi.addFavourite(fund.fundKey);
      }
    } catch {
      setFund((prev) => (prev ? { ...prev, favourited: wasFavourited } : prev));
    }
  };

  const calendarRows: CalendarYearRow[] = fund?.calendarYears
    ? Object.entries(fund.calendarYears)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([year, value]) => ({ year, value }))
    : [];

  const calendarColumns: DataTableColumn<CalendarYearRow>[] = [
    { key: 'year', header: 'Year', width: '120px', render: (r) => r.year },
    { key: 'value', header: 'Performance', render: (r) => formatPctString(r.value) },
  ];

  const extra = fund?.extraFields ?? null;
  const typedKeyFacts: ResolvedField[] = fund ? buildTypedKeyFacts(fund) : [];
  const keyFacts: ResolvedField[] = [...typedKeyFacts, ...pickFieldSpecs(extra, 'keyFacts')];
  const overviewFields: ResolvedField[] = pickFieldSpecs(extra, 'overview');
  const documentFields: ResolvedField[] = pickFieldSpecs(extra, 'documents');
  const additionalFields: ResolvedField[] = additionalScalarFields(extra);
  const breakdowns: { heading: string; rows: BreakdownRow[] }[] = [];
  if (extra) {
    const seenHeadings = new Set<string>();
    for (const [key, heading] of BREAKDOWN_SOURCES) {
      if (seenHeadings.has(heading)) continue;
      const rows = normalizeBreakdown(extra[key]);
      if (rows && rows.length > 0) {
        breakdowns.push({ heading, rows });
        seenHeadings.add(heading);
      }
    }
  }

  if (loading) {
    return (
      <div className="mx-lg my-lg" data-testid="fund-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-lg my-lg bg-white rounded-card shadow-card p-lg flex flex-col gap-md items-start">
        <h1 className="m-0 text-body-01 font-medium">Fund not found</h1>
        <p className="m-0 text-body-03 text-grey-02">
          No fund with key <span className="font-mono">{fundKey}</span> exists in the catalogue — it
          may have been removed by its provider.
        </p>
        <Button variant="outlined" size="md" onClick={() => navigate('/funds')}>
          Back to Fund Center
        </Button>
      </div>
    );
  }

  if (error || !fund) {
    return (
      <div className="mx-lg my-lg">
        <Alert variant="error">{error ?? 'Failed to load fund'}</Alert>
        <div className="mt-md">
          <Button variant="outlined" size="md" onClick={() => navigate('/funds')}>
            Back to Fund Center
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-lg my-lg flex flex-col gap-lg" data-testid="fund-detail-page">
      <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-lg">
        <div className="flex items-start justify-between gap-md flex-wrap">
          <div className="flex flex-col gap-sm min-w-0">
            <div className="flex items-center gap-sm flex-wrap">
              <IconButton
                variant="ghost"
                aria-label="Back to Fund Center"
                onClick={() => navigate('/funds')}
                icon={<AppIcon name="arrow left" size={20} />}
              />
              <h1 className="m-0 text-body-01 font-medium">{fund.fundName}</h1>
              {fund.isOpen != null && (
                <Badge color={fund.isOpen ? 'green' : 'grey'} size="sm">
                  {fund.isOpen ? 'Open' : 'Closed'}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-md flex-wrap text-body-03 text-grey-02">
              <span>{fund.providerDisplayName || fund.providerCode}</span>
              {fund.categoryName && <Badge color="neutral" size="sm">{fund.categoryName}</Badge>}
              {fund.riskRating != null && (
                <span className="flex items-center gap-xs" data-testid="fund-detail-risk">
                  <span>Risk</span>
                  <RiskRatingChip rating={fund.riskRating} raw={fund.riskRatingRaw} />
                </span>
              )}
              <span className="font-mono text-body-04">{fund.fundKey}</span>
            </div>
            {tagsFeatureEnabled && (
              <FundTagEditor
                fundKey={fund.fundKey}
                tagIds={fund.tagIds}
                onTagIdsChange={(tagIds) => setFund((prev) => (prev ? { ...prev, tagIds } : prev))}
              />
            )}
          </div>
          <div className="flex items-center gap-sm">
            <IconButton
              variant="ghost"
              aria-label={fund.favourited ? 'Remove favourite' : 'Add favourite'}
              onClick={toggleFavourite}
              icon={<AppIcon name="heart" size={22} variant={fund.favourited ? 'Bold' : 'Linear'} />}
            />
            {fund.factsheetUrl && (
              <Button
                variant="outlined"
                size="md"
                onClick={() => window.open(fund.factsheetUrl!, '_blank', 'noopener,noreferrer')}
              >
                Factsheet
              </Button>
            )}
          </div>
        </div>

        <StatTileRow card={false} data-testid="fund-detail-price-tiles">
          <StatTile
            label="Price"
            value={fund.price != null ? fund.price.toFixed(4) : null}
            subText={fund.priceDate ? `as of ${formatDate(fund.priceDate)}` : undefined}
          />
          <StatTile label="Launch date" value={fund.launchDate ? formatDate(fund.launchDate) : null} />
          <StatTile
            label="Performance date"
            value={fund.performanceDate ? formatDate(fund.performanceDate) : null}
          />
        </StatTileRow>
      </div>

      {keyFacts.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md" data-testid="fund-detail-key-facts">
          <h2 className="m-0 text-body-02 font-medium">Key facts</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-lg gap-y-sm m-0">
            {keyFacts.map((f) => (
              <div key={f.label} className="flex justify-between gap-md py-xs border-b border-grey-06">
                <dt className="text-body-03 text-grey-02">{f.label}</dt>
                <dd className="m-0 text-body-03 font-medium text-right">{f.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {overviewFields.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md" data-testid="fund-detail-overview">
          <h2 className="m-0 text-body-02 font-medium">Overview</h2>
          {overviewFields.map((f) => (
            <div key={f.label} className="flex flex-col gap-xs">
              <span className="text-body-04 text-grey-02 uppercase">{f.label}</span>
              <p className="m-0 text-body-03">{f.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md">
        <h2 className="m-0 text-body-02 font-medium">Annualised performance</h2>
        <StatTileRow card={false} data-testid="fund-detail-perf-tiles">
          <StatTile label="1 Month" value={formatPct(fund.perf1mPct)} />
          <StatTile label="3 Months" value={formatPct(fund.perf3mPct)} />
          <StatTile label="1 Year" value={formatPct(fund.perf1yPct)} />
          <StatTile label="3 Years" value={formatPct(fund.perf3yPct)} />
          <StatTile label="5 Years" value={formatPct(fund.perf5yPct)} />
          <StatTile label="10 Years" value={formatPct(fund.perf10yPct)} />
          <StatTile label="Since launch" value={formatPct(fund.perfSinceLaunchPct)} />
        </StatTileRow>
      </div>

      {calendarRows.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md">
          <h2 className="m-0 text-body-02 font-medium">Calendar-year performance</h2>
          <DataTable
            columns={calendarColumns}
            data={calendarRows}
            rowKey={(r) => r.year}
            emptyState={<span>No calendar-year history.</span>}
          />
        </div>
      )}

      {breakdowns.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-lg" data-testid="fund-detail-composition">
          <h2 className="m-0 text-body-02 font-medium">Composition</h2>
          {breakdowns.map((b) => (
            <div key={b.heading} className="flex flex-col gap-sm">
              <h3 className="m-0 text-body-03 font-medium text-grey-02">{b.heading}</h3>
              <div className="flex flex-col gap-xs">
                {b.rows.map((r, i) => (
                  <div key={`${r.label}-${i}`} className="flex justify-between gap-md py-xs border-b border-grey-06">
                    <span className="text-body-03">{r.label}</span>
                    <span className="text-body-03 font-medium">{r.percent.toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {documentFields.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md" data-testid="fund-detail-documents">
          <h2 className="m-0 text-body-02 font-medium">Documents</h2>
          <div className="flex flex-col gap-sm">
            {documentFields.map((f) => (
              <a
                key={f.label}
                href={f.value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-03 text-purple-primary underline"
              >
                {f.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {additionalFields.length > 0 && (
        <div className="bg-white rounded-card shadow-card p-lg flex flex-col gap-md" data-testid="fund-detail-additional">
          <h2 className="m-0 text-body-02 font-medium">Additional details</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-lg gap-y-sm m-0">
            {additionalFields.map((f) => (
              <div key={f.label} className="flex justify-between gap-md py-xs border-b border-grey-06">
                <dt className="text-body-03 text-grey-02">{f.label}</dt>
                <dd className="m-0 text-body-03 font-medium text-right break-words min-w-0">
                  {f.isUrl ? (
                    <a href={f.value} target="_blank" rel="noopener noreferrer" className="text-purple-primary underline">
                      Link
                    </a>
                  ) : (
                    f.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

/** Typed FundDTO key-fact promotions → display rows (hidden when absent). */
function buildTypedKeyFacts(fund: FundDTO): ResolvedField[] {
  const out: ResolvedField[] = [];
  if (fund.sfdr) out.push({ label: 'SFDR', value: `Article ${fund.sfdr}`, isUrl: false });
  if (fund.assetClass) out.push({ label: 'Asset class', value: fund.assetClass, isUrl: false });
  if (fund.fundManager) out.push({ label: 'Fund manager', value: fund.fundManager, isUrl: false });
  if (fund.fundSize != null) out.push({ label: 'Fund size', value: `€${fund.fundSize}m`, isUrl: false });
  if (fund.amc != null) out.push({ label: 'AMC', value: `${fund.amc}%`, isUrl: false });
  if (fund.ocf != null) out.push({ label: 'OCF', value: `${fund.ocf}%`, isUrl: false });
  return out;
}

function formatPct(value: number | null): string {
  if (value == null) return '—';
  const formatted = value.toFixed(2);
  return value > 0 ? `+${formatted}%` : `${formatted}%`;
}

function formatPctString(value: string): string {
  if (value == null || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? formatPct(parsed) : value;
}
